import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { nextParam } from "@/lib/nextDest";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, GoogleLogo, User, Buildings, X, GraduationCap } from "@phosphor-icons/react";
import { firebaseReady, googleSignIn, humanizeFirebaseError } from "@/lib/firebase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import CountrySelect from "@/components/CountrySelect";
import { SocialPulseButton } from "@/components/SocialPulseButton";

const POLICIES = [
  { key: "terms", label: "Terms of Use", to: "/terms" },
  { key: "privacy", label: "Privacy Policy", to: "/privacy" },
  { key: "cookie", label: "Cookie Policy", to: "/cookie" },
  { key: "memorial", label: "Memorial & Obituary Policy", to: "/memorial-policy" },
];
const YEARS = Array.from({ length: 90 }, (_, i) => String(new Date().getFullYear() - 13 - i));

export default function Register() {
  const { register, firebaseLogin, user, ready } = useAuth();
  const nav = useNavigate();
  // Redirect a visitor who is ALREADY signed in when they land here (once auth
  // has loaded). A brand-new signup is navigated by the submit/google handlers
  // (to onboarding or ?next=); we depend only on `ready` so this never fires
  // mid-signup and competes with those handlers.
  useEffect(() => {
    if (ready && user) nav(nextParam() || "/dashboard", { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);
  const [accountType, setAccountType] = useState("individual");
  const [f, setF] = useState({ first_name: "", last_name: "", org_name: "", org_address: "", contact_person: "", org_email: "", org_phone: "", gender: "", year_of_birth: "", country: "", state: "", city: "", email: "", password: "" });
  const [consents, setConsents] = useState({ terms: false, privacy: false, cookie: false, memorial: false });
  const [keep, setKeep] = useState(true);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const close = () => nav("/");

  const allConsents = POLICIES.every((p) => consents[p.key]);
  const nameOk = accountType === "org" ? f.org_name.trim() : f.first_name.trim();
  const canSubmit = allConsents && nameOk && f.email.trim() && f.password.length >= 6;

  const buildPayload = () => ({
    ...f, account_type: accountType,
    name: accountType === "org" ? f.org_name : `${f.first_name} ${f.last_name}`.trim(),
    consents, keep_signed_in: keep,
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!allConsents) { toast.error("Please accept all four policies to continue"); return; }
    setLoading(true);
    const dest = nextParam() || "/onboarding";
    if (nextParam()) { try { sessionStorage.setItem("ia_just_joined", "1"); } catch { /* ignore */ } }
    try {
      await register(buildPayload());
      toast.success("Account created!");
      nav(dest);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const google = async () => {
    if (!allConsents) { toast.error("Please accept all four policies first"); return; }
    const dest = nextParam() || "/onboarding";
    if (nextParam()) { try { sessionStorage.setItem("ia_just_joined", "1"); } catch { /* ignore */ } }
    try {
      const idToken = await googleSignIn();
      await firebaseLogin(idToken, keep);
      toast.success("Welcome to AlumniPulse!");
      nav(dest);
    } catch (err) { toast.error(humanizeFirebaseError(err)); }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-black/60 p-4 backdrop-blur-sm sm:p-6"
      data-testid="register-modal"
      onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}
    >
      <div className="relative my-4 w-full max-w-md rounded-2xl border border-border/60 bg-card p-6 shadow-2xl sm:my-8 sm:p-8">
        <button type="button" onClick={close} aria-label="Close" data-testid="register-close"
          className="absolute right-3 top-3 grid h-9 w-9 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground">
          <X size={18} />
        </button>
        <div className="mb-6 flex items-center gap-2.5 pr-8">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-primary text-primary-foreground"><GraduationCap weight="fill" size={20} /></span>
          <div>
            <h1 className="font-heading text-xl font-semibold leading-tight">Register &amp; Join AlumniPulse™</h1>
            <p className="mt-0.5 text-xs text-muted-foreground">Powered by SocialPulse® — one account across the network.</p>
          </div>
        </div>
        <form onSubmit={submit} className="space-y-5" data-testid="register-form">
        {/* Account type */}
        <div className="grid grid-cols-2 gap-2 rounded-full border border-border/60 p-1" data-testid="account-type-toggle">
          {[["individual", "Individual", User], ["org", "Institution / Org", Buildings]].map(([val, label, Icon]) => (
            <button type="button" key={val} onClick={() => setAccountType(val)} data-testid={`account-type-${val}`}
              className={`flex items-center justify-center gap-1.5 rounded-full px-3 py-2 text-sm font-medium transition-colors ${accountType === val ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
              <Icon size={16} /> {label}
            </button>
          ))}
        </div>

        {accountType === "org" ? (
          <div className="space-y-3 rounded-xl border border-border/60 bg-muted/20 p-4" data-testid="org-details">
            <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Institution / Org details — you can add more from your institution/org profile later</p>
            <div className="space-y-1.5">
              <Label htmlFor="org">Institution / Org name <span className="text-secondary">*</span></Label>
              <Input id="org" value={f.org_name} onChange={set("org_name")} data-testid="register-org-name" placeholder="e.g. Springfield High School" />
            </div>
            <div className="space-y-1.5">
              <Label>Institution / Org address</Label>
              <Input value={f.org_address} onChange={set("org_address")} data-testid="register-org-address" placeholder="Street, city — enough for people to find you" />
            </div>
            <div className="space-y-1.5">
              <Label>Contact person</Label>
              <Input value={f.contact_person} onChange={set("contact_person")} data-testid="register-contact-person" placeholder="Auto-filled from your name — edit if different" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Institution / Org email</Label><Input type="email" value={f.org_email} onChange={set("org_email")} data-testid="register-org-email" placeholder="hello@myschool.org" /></div>
              <div className="space-y-1.5"><Label>Institution / Org phone</Label><Input value={f.org_phone} onChange={set("org_phone")} data-testid="register-org-phone" placeholder="+1 555 123 4567" /></div>
            </div>
            <p className="text-xs text-muted-foreground">You'll sign in with your own email/phone — the institution/org gets its own profile page where you can add team members with Manager or Editor access.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2"><Label>First name</Label><Input value={f.first_name} onChange={set("first_name")} data-testid="register-first-name" placeholder="Aisha" /></div>
            <div className="space-y-2"><Label>Last name</Label><Input value={f.last_name} onChange={set("last_name")} data-testid="register-last-name" placeholder="Menon" /></div>
          </div>
        )}

        {accountType === "individual" && (
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Gender</Label>
              <Select value={f.gender} onValueChange={(v) => setF({ ...f, gender: v })}>
                <SelectTrigger data-testid="register-gender"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{["Female", "Male", "Non-binary", "Prefer not to say"].map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Year of birth</Label>
              <Select value={f.year_of_birth} onValueChange={(v) => setF({ ...f, year_of_birth: v })}>
                <SelectTrigger data-testid="register-yob"><SelectValue placeholder="Year" /></SelectTrigger>
                <SelectContent className="max-h-60">{YEARS.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        )}

        {/* Location */}
        <div className="space-y-2">
          <Label>Confirm your location</Label>
          <CountrySelect value={f.country} onChange={(v) => setF({ ...f, country: v })} testId="register-country" />
          <div className="grid grid-cols-2 gap-3">
            <Input value={f.state} onChange={set("state")} data-testid="register-state" placeholder="State / region" />
            <Input value={f.city} onChange={set("city")} data-testid="register-city" placeholder="City" />
          </div>
        </div>

        {/* Credentials */}
        <div className="space-y-2"><Label htmlFor="email">Email</Label><Input id="email" type="email" value={f.email} onChange={set("email")} required data-testid="register-email" placeholder="you@example.com" /></div>
        <div className="space-y-2"><Label htmlFor="password">Password</Label><Input id="password" type="password" value={f.password} onChange={set("password")} required minLength={6} data-testid="register-password" placeholder="At least 6 characters" /></div>

        {/* Policies */}
        <div className="space-y-2 rounded-xl border border-border/60 bg-muted/20 p-4" data-testid="policy-consents">
          <p className="text-xs font-medium text-muted-foreground">Please review and accept:</p>
          {POLICIES.map((p) => (
            <label key={p.key} className="flex items-center gap-2.5 text-sm">
              <Checkbox checked={consents[p.key]} onCheckedChange={(v) => setConsents({ ...consents, [p.key]: !!v })} data-testid={`consent-${p.key}`} />
              <span>I agree to the{" "}
                <a href={p.to} target="_blank" rel="noreferrer" className="font-medium text-secondary hover:underline" data-testid={`policy-link-${p.key}`}>{p.label}</a>
              </span>
            </label>
          ))}
        </div>

        <label className="flex items-center gap-2.5 text-sm text-muted-foreground">
          <Checkbox checked={keep} onCheckedChange={(v) => setKeep(!!v)} data-testid="keep-signed-in" /> Keep me signed in <span className="text-xs">(12h idle limit)</span>
        </label>

        <Button type="submit" className="w-full rounded-full gap-2" disabled={loading || !canSubmit} data-testid="register-submit">
          {loading ? "Creating…" : "Create account"} <ArrowRight size={16} />
        </Button>

        <SocialPulseButton />
        {firebaseReady && (
          <>
            <div className="flex items-center gap-3 text-xs text-muted-foreground"><span className="h-px flex-1 bg-border" /> or <span className="h-px flex-1 bg-border" /></div>
            <Button type="button" variant="outline" onClick={google} className="w-full rounded-full gap-2" data-testid="register-google"><GoogleLogo size={17} weight="bold" /> Continue with Google</Button>
          </>
        )}

        <p className="text-center text-sm text-muted-foreground">
          Already a member? <Link to="/login" className="font-medium text-secondary hover:underline">Sign in</Link>
        </p>
        </form>
      </div>
    </div>
  );
}
